import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { ContentWrapperCustomerApp } from '@components/Layouts/ContentWrapperCustomerApp';
import { NoddiLoadingScreenWithTrans } from '@components/Loading/NoddiLoadingScreenWithTrans';
import { URLKeys, noddiAsync } from 'noddi-async';
import { NoddiBasicCard, TablePagination, usePaginationQueryParam } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import { VinNumberDetailsDialog, useAuthContext } from 'noddi-web';
import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getCarName } from '../../../utils/carName';
import { getCommonTranslations } from '../../../utils/commonTranslations';

import { CarBookings } from './CarBookings';
import { CarFacts } from './CarFacts';
import { CarHero } from './CarHero';
import { CarInspections } from './CarInspections';
import { CarTireSeason, getCurrentSeason } from './seasonTreadDepth';
import { TireDetails } from './TireDetails';
import { UpcomingCarBooking } from './UpcomingCarBooking';

export const CarDetailPage = () => {
  const { currentUserGroupId: userGroupId } = useAuthContext();
  const [vinDetailsDialogOpen, setVinDetailsDialogOpen] = useState(false);
  const [tireSeason, setTireSeason] = useState<CarTireSeason>(getCurrentSeason);
  const tiresRef = useRef<HTMLDivElement>(null);
  const factsRef = useRef<HTMLDivElement>(null);
  const { carId } = useParams();
  const carIdAsNumber = parseInt(carId!);
  const { pagination, setPagination } = usePaginationQueryParam({ initialPageSize: 1 });

  const {
    data: car,
    isPending,
    error: carError
  } = noddiAsync.useGet({
    type: URLKeys.getUserGroupCar,
    input: { userGroupId: userGroupId!, carId: carIdAsNumber }
  });

  const {
    data: latestTreadDepth,
    isPending: isLatestTreadDepthPending,
    error: latestTreadDepthError
  } = noddiAsync.useGet({
    type: URLKeys.getLatestCarWheelMeasurements,
    input: { carId: carIdAsNumber }
  });

  const {
    data: carInspections,
    isLoading: isCarInspectionsLoading,
    error: carInspectionsError
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedInspectionSessions,
    input: { carIds: [carIdAsNumber], ...pagination },
    queryConfig: {
      // TODO : toggle this feature on when api permission is fixed
      // https://app.clickup.com/t/86c5wnpvk
      enabled: false
    }
  });

  if (isPending || isLatestTreadDepthPending || isCarInspectionsLoading) {
    return <NoddiLoadingScreenWithTrans />;
  }
  if (carError) {
    return <ApiErrorPageWithTrans error={carError} />;
  }
  if (latestTreadDepthError) {
    return <ApiErrorPageWithTrans error={latestTreadDepthError} />;
  }
  if (carInspectionsError) {
    return <ApiErrorPageWithTrans error={carInspectionsError} />;
  }

  const { vinNumberModel } = car;

  // The hero shows the plate as its own badge, so the title is make and model only.
  const title = getCarName({ car });
  return (
    <ContentWrapperCustomerApp hideBackButton>
      {/* VIN decode is a customer curiosity (WMI, year, plant), not staff-only. Don't strip it. */}
      {vinNumberModel && (
        <VinNumberDetailsDialog
          vinNumberModel={vinNumberModel}
          open={vinDetailsDialogOpen}
          onOpenChange={setVinDetailsDialogOpen}
          translations={getCommonTranslations().vinNumberDetails}
        />
      )}
      <div className='flex flex-col gap-10 md:gap-12'>
        <CarHero
          car={car}
          title={title}
          latestTreadDepth={latestTreadDepth}
          onSeasonClick={(season) => {
            setTireSeason(season);
            scrollToSection(tiresRef.current);
          }}
          onEuControlClick={() => scrollToSection(factsRef.current)}
        />
        <UpcomingCarBooking userGroupId={userGroupId!} carId={carIdAsNumber} />
        <div className='grid grid-cols-1 items-start gap-5 md:grid-cols-2'>
          <div ref={tiresRef} className='scroll-mt-6'>
            <TireDetails
              to={customerRoutes.myCarWheelMeasurements.getPath({ carId: carId! })}
              latestTreadDepth={latestTreadDepth}
              season={tireSeason}
              onSeasonChange={setTireSeason}
            />
          </div>
          <div ref={factsRef} className='scroll-mt-6'>
            <CarFacts car={car} onVinNumberClick={vinNumberModel ? () => setVinDetailsDialogOpen(true) : undefined} />
          </div>
        </div>
        <CarBookings userGroupId={userGroupId!} carId={carIdAsNumber} />
      </div>
      {carInspections && (
        <>
          {/* Car Inspections Section */}
          <div className='mt-8'>
            <CarInspections carInspections={carInspections} isLoading={isCarInspectionsLoading} />

            {carInspections && carInspections.count > 1 && (
              <NoddiBasicCard className='mt-4'>
                <TablePagination
                  pagination={pagination}
                  totalCount={carInspections.count}
                  onChange={({ pageIndex, pageSize }) =>
                    setPagination({ pageIndex, pageSize, search: pagination.search })
                  }
                  loading={isCarInspectionsLoading}
                  translations={getCommonTranslations().table}
                />
              </NoddiBasicCard>
            )}
          </div>
        </>
      )}
    </ContentWrapperCustomerApp>
  );
};

const scrollToSection = (section: HTMLElement | null) =>
  section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
